import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, UserPlus, UserMinus, MapPin, ChevronRight, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getDefaultPhoto = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'G')}&background=0F3B2E&color=fff&size=128&font-size=0.4&bold=true`;
const getPhotoUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const UserSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [followLoading, setFollowLoading] = useState({});
  const [dmLoading, setDmLoading] = useState({});

  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    try {
      setSearching(true);
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch (err) {
      console.error('User search failed:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    // Debounce
    clearTimeout(window._searchTimeout);
    window._searchTimeout = setTimeout(() => searchUsers(val), 300);
  };

  const toggleFollow = async (userId, isFollowing) => {
    setFollowLoading(prev => ({ ...prev, [userId]: true }));
    try {
      if (isFollowing) {
        await axios.delete(`${API}/follow/${userId}`);
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/follow/${userId}`);
        toast.success('Following!');
      }
      setResults(prev => prev.map(u =>
        u.id === userId ? { ...u, is_following: !isFollowing } : u
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const paceLabels = { ready_golf: 'Ready Golf', quick: 'Quick', average: 'Average', deliberate: 'Deliberate' };

  const startDM = async (userId) => {
    setDmLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.post(`${API}/conversations/start`, { user_id: userId });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start conversation');
    } finally {
      setDmLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="user-search-page">
      <div className="px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <h1 className="font-serif text-2xl font-bold text-foreground">Find Golfers</h1>
        <p className="text-xs text-muted-foreground">Search and follow players</p>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or username..."
            value={query}
            onChange={handleSearch}
            className="pl-10 h-11 rounded-xl"
            data-testid="user-search-input"
          />
        </div>

        {/* Results */}
        {searching ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-muted">
                <div className="w-12 h-12 rounded-full bg-muted-foreground/20" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 rounded w-1/3 bg-muted-foreground/20" />
                  <div className="h-3 rounded w-1/2 bg-muted-foreground/20" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 && query.length > 0 ? (
          <div className="text-center py-12">
            <Search className="w-10 h-10 mx-auto mb-3 text-muted" />
            <p className="text-sm text-muted-foreground">No golfers found for "{query}"</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-10 h-10 mx-auto mb-3 text-muted" />
            <h3 className="font-serif text-lg font-semibold mb-1 text-foreground">Find Your Playing Partners</h3>
            <p className="text-sm text-muted-foreground">Search by name to follow golfers and see their posts in your feed</p>
          </div>
        ) : (
          <div className="space-y-2">
            {results.map(user => (
              <div
                key={user.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card transition-colors"
                data-testid={`search-result-${user.id}`}
              >
                <Avatar
                  className="w-12 h-12 border-2 border-border cursor-pointer"
                  onClick={() => navigate(`/user/${user.id}`)}
                >
                  <AvatarImage src={getPhotoUrl(user.profile_photo) || getDefaultPhoto(user.display_name)} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user.display_name?.charAt(0) || 'G'}
                  </AvatarFallback>
                </Avatar>

                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`/user/${user.id}`)}
                >
                  <h3 className="font-semibold text-sm truncate text-foreground">{user.display_name || user.username}</h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {user.location && (
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" /> {user.location}
                      </span>
                    )}
                    {user.handicap != null && (
                      <span>{user.handicap} Hdcp</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full text-xs h-8 w-8 p-0"
                    onClick={() => startDM(user.id)}
                    disabled={dmLoading[user.id]}
                    data-testid={`dm-btn-${user.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={user.is_following ? "outline" : "default"}
                    className="rounded-full text-xs gap-1 h-8 px-3"
                    onClick={() => toggleFollow(user.id, user.is_following)}
                    disabled={followLoading[user.id]}
                    data-testid={`follow-btn-${user.id}`}
                  >
                    {user.is_following ? (
                      <><UserMinus className="w-3.5 h-3.5" /> Following</>
                    ) : (
                      <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Navigation />
    </div>
  );
};

export default UserSearch;
